/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {PasswordInput} from 'UI/components/action/PasswordInput';

import {useLoginPage} from 'UI/hooks/useLoginPage';
import {AuthLayout} from 'UI/layouts/AuthLayout';

import type {GetStaticProps} from 'next';
import type {NextSSGPageWithLayout} from 'types/global';

/**
 * Login-Seite. Ermöglicht die Anmeldung über die Payload CMS User-Authentifizierung.
 * Leitet authentifizierte User automatisch zur Account-Seite weiter.
 *
 * @returns ReactNode der Login-Seite.
 */
const LoginPage: NextSSGPageWithLayout<typeof getStaticProps> = () => {
    const {
        email,
        error,
        handleEmailChange,
        handlePasswordChange,
        handleSubmit,
        isLoading,
        password,
        registerHref,
        submitting,
        t,
        user
    } = useLoginPage();

    if (isLoading || user) {
        return <LoadingText data-cy="LoginPage-loading">{t('common.loading')}</LoadingText>;
    }

    return (
        <div data-cy="LoginPage">
            <PageTitle>{t('login.title')}</PageTitle>
            <Form data-cy="LoginPage-form" onSubmit={handleSubmit}>
                {error && <ErrorMessage data-cy="LoginPage-error">{error}</ErrorMessage>}
                <Label>
                    {t('login.email')}
                    <Input
                        autoComplete="email"
                        data-cy="LoginPage-email"
                        type="email"
                        value={email}
                        required
                        onChange={handleEmailChange}
                    />
                </Label>
                <Label>
                    {t('login.password')}
                    <PasswordInput
                        autoComplete="current-password"
                        testId="LoginPage-password"
                        value={password}
                        required
                        onChange={handlePasswordChange}
                    />
                </Label>
                <SubmitButton data-cy="LoginPage-submit" disabled={submitting} type="submit">
                    {submitting ? t('login.submitting') : t('login.submit')}
                </SubmitButton>
            </Form>
            <FooterText>
                {t('login.noAccount')} <Link href={registerHref}>{t('login.register')}</Link><br />
                {t('login.noPassword')} <Link href="/account/forgot-password">{t('login.forgotPassword')}</Link>
            </FooterText>
        </div>
    );
};

/**
 * `getLayout` wraps die Seite mit dem Auth-Layout (zentrierter Wrapper).
 *
 * @param _route        Die aktuelle Route.
 * @param pageProps     Die Seiten-Props.
 * @param PageComponent Die Seitenkomponente.
 * @returns ReactNode mit AuthLayout.
 */
LoginPage.getLayout = (_route, pageProps, PageComponent) => (
    <AuthLayout>
        <PageComponent {...pageProps} />
    </AuthLayout>
);

/**
 * `getLayoutKey` gibt den Layout-Schlüssel zurück.
 *
 * @returns Der Layout-Key-String.
 */
LoginPage.getLayoutKey = () => 'auth';

export default LoginPage;

// ── Styled ──────────────────────────────────────────────────

const LoadingText = styled.p`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    text-align: center;
`;

const PageTitle = styled.h1`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 2.8rem;
    margin-bottom: ${spacing(8)};
    text-align: center;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${spacing(5)};
`;

const ErrorMessage = styled.div`
    background: ${({theme}) => theme.colors.errorSurface};
    border: 1px solid ${({theme}) => theme.colors.error};
    border-radius: 8px;
    color: ${({theme}) => theme.colors.error};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    padding: ${spacing(3)} ${spacing(4)};
`;

const Label = styled.label`
    display: flex;
    flex-direction: column;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    font-weight: 700;
    gap: ${spacing(1.5)};
`;

const Input = styled.input`
    border: 1px solid ${({theme}) => theme.colors.inkDisabled};
    border-radius: 8px;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-weight: 400;
    padding: ${spacing(3)} ${spacing(4)};
`;

const SubmitButton = styled.button`
    background: ${({theme}) => theme.colors.primaryDefault};
    border: none;
    border-radius: 8px;
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    margin-top: ${spacing(2)};
    padding: ${spacing(3.5)};

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const FooterText = styled.p`
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    margin-top: ${spacing(6)};
    text-align: center;

    a {
        color: ${({theme}) => theme.colors.primaryDefault};
        text-decoration: underline;
    }
`;

/**
 * Statische Props für die Login-Seite.
 *
 * @returns Leere Props mit Revalidierung.
 */
export const getStaticProps: GetStaticProps = async () => Promise.resolve({
    props: {},
    revalidate: 18000
});